import { useCallback, useMemo } from 'react';
import { Button } from 'src/v2/commonComponents/Button';
import { convertTierName } from 'src/utils/user';
import { Modal } from 'src/components/Modal';
import { useGetUser, useSubscriptionInfo } from 'src/hooks';
import {
  AVAILABLE_FEATURES_LIST,
  FEATURES_ON_CANCELLATION,
  getSuperNinjaUltraFeatures,
  MY_NINJA_BUSINESS_ULTRA_FEATURES_VARIANT_A,
  MY_NINJA_STANDARD_FEATURES,
  MY_NINJA_ULTRA_FEATURES_VARIANT_A,
  NEW_FEATURES_ON_CANCELLATION,
  NEW_PLANS_VARIANTS,
  PRO_FEATURES,
  STANDARD_FEATURES,
  SVG_SIZE_M,
  ULTRA_FEATURES,
} from 'src/constants';
import { X } from '@phosphor-icons/react';
import styles from './CancelSubscriptionModal.module.scss';
import { useNavigate } from 'react-router-dom';
import { AppRoutes, PlanKind } from 'src/types';

type CancelSubscriptionModalProps = {
  cancellationDate?: string;
  isOpen: boolean;
  onClose: () => void;
  planKind?: PlanKind;
};

const isNewPlanPage = AVAILABLE_FEATURES_LIST.newSuperNinjaPlans;

export const CancelSubscriptionModal = ({
  cancellationDate,
  isOpen,
  onClose,
  planKind,
}: CancelSubscriptionModalProps) => {
  const {
    tier_id,
    isStandardTier,
    isProTier,
    isTeamsTier,
    isSuperNinjaUltraTier,
  } = useGetUser();

  const { subscriptionInfo } = useSubscriptionInfo();

  const isNewVariant = subscriptionInfo?.plan_variant
    ? NEW_PLANS_VARIANTS.includes(subscriptionInfo?.plan_variant)
    : false;

  const navigate = useNavigate();

  const handleChange = useCallback(async () => {
    navigate(AppRoutes.CANCEL_SUBSCRIPTION);
  }, [navigate]);

  // todo remove in the future
  const oldFeatures = useMemo(() => {
    if (isStandardTier) {
      return isNewPlanPage
        ? MY_NINJA_STANDARD_FEATURES.slice(1)
        : STANDARD_FEATURES;
    }
    if (isProTier) {
      return PRO_FEATURES;
    }
    if (isTeamsTier && isNewPlanPage) {
      return MY_NINJA_BUSINESS_ULTRA_FEATURES_VARIANT_A.slice(1);
    }
    if (isSuperNinjaUltraTier) {
      return getSuperNinjaUltraFeatures()
        .slice(1)
        .filter((item) => item.id !== '2');
    }
    return isNewPlanPage
      ? MY_NINJA_ULTRA_FEATURES_VARIANT_A.slice(1)
      : ULTRA_FEATURES;
  }, [isStandardTier, isProTier, isSuperNinjaUltraTier, isTeamsTier]);

  const features = useMemo(() => {
    switch (planKind) {
      case PlanKind.STANDARD_MONTHLY:
      case PlanKind.STANDARD_ANNUALLY:
      case PlanKind.PRO_MONTHLY:
      case PlanKind.PRO_ANNUALLY:
      case PlanKind.PRO_10_ANNUALLY:
      case PlanKind.PRO_10_MONTHLY:
        return FEATURES_ON_CANCELLATION.PRO_10;
      case PlanKind.ULTRA_MONTHLY:
      case PlanKind.ULTRA_ANNUALLY:
      case PlanKind.PRO_30_ANNUALLY:
      case PlanKind.PRO_30_MONTHLY:
        return FEATURES_ON_CANCELLATION.PRO_30;
      case PlanKind.SUPERNINJA_ULTRA_MONTHLY_10:
      case PlanKind.SUPERNINJA_ULTRA_ANNUALLY_10:
      case PlanKind.MAX_10_MONTHLY:
      case PlanKind.MAX_10_ANNUALLY:
        return FEATURES_ON_CANCELLATION.MAX_10;
      case PlanKind.MAX_20_MONTHLY:
      case PlanKind.MAX_20_ANNUALLY:
      case PlanKind.SUPERNINJA_ULTRA_MONTHLY_20:
      case PlanKind.SUPERNINJA_ULTRA_ANNUALLY_20:
        return FEATURES_ON_CANCELLATION.MAX_20;
      case PlanKind.SUPERNINJA_ULTRA_MONTHLY:
      case PlanKind.SUPERNINJA_ULTRA_ANNUALLY:
      case PlanKind.MAX_30_MONTHLY:
      case PlanKind.MAX_30_ANNUALLY:
        return FEATURES_ON_CANCELLATION.MAX_30;
      case PlanKind.BUSINESS_10_MONTHLY:
      case PlanKind.BUSINESS_10_ANNUALLY:
        return FEATURES_ON_CANCELLATION.BUSINESS_10;
      case PlanKind.BUSINESS_20_MONTHLY:
      case PlanKind.BUSINESS_20_ANNUALLY:
        return FEATURES_ON_CANCELLATION.BUSINESS_20;
      case PlanKind.BUSINESS_30_MONTHLY:
      case PlanKind.BUSINESS_30_ANNUALLY:
        return FEATURES_ON_CANCELLATION.BUSINESS_30;
      default:
        return oldFeatures;
    }
  }, [planKind, oldFeatures]);

  const newPlansFeatures = useMemo(() => {
    switch (planKind) {
      case PlanKind.MAX_05_MONTHLY:
      case PlanKind.MAX_05_ANNUALLY:
        return NEW_FEATURES_ON_CANCELLATION.MAX_05;
      case PlanKind.MAX_10_MONTHLY:
      case PlanKind.MAX_10_ANNUALLY:
        return NEW_FEATURES_ON_CANCELLATION.MAX_10;
      case PlanKind.MAX_20_MONTHLY:
      case PlanKind.MAX_20_ANNUALLY:
        return NEW_FEATURES_ON_CANCELLATION.MAX_20;
      case PlanKind.MAX_30_MONTHLY:
      case PlanKind.MAX_30_ANNUALLY:
        return NEW_FEATURES_ON_CANCELLATION.MAX_30;
      case PlanKind.BUSINESS_10_MONTHLY:
      case PlanKind.BUSINESS_10_ANNUALLY:
        return NEW_FEATURES_ON_CANCELLATION.BUSINESS_10;
      case PlanKind.BUSINESS_20_MONTHLY:
      case PlanKind.BUSINESS_20_ANNUALLY:
        return NEW_FEATURES_ON_CANCELLATION.BUSINESS_20;
      case PlanKind.BUSINESS_30_MONTHLY:
      case PlanKind.BUSINESS_30_ANNUALLY:
        return NEW_FEATURES_ON_CANCELLATION.BUSINESS_30;
      default:
        return oldFeatures;
    }
  }, [planKind, oldFeatures]);

  const currentPlan = tier_id ? convertTierName(tier_id) : '';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
      blockScroll={false}
    >
      <div className={styles.root}>
        <p className={styles.title}>Cancel subscription?</p>

        <p className={styles.body}>
          <span>
            By cancelling your {currentPlan} subscription, you will lose access
            to {currentPlan} features and your plan will be downgraded to Basic
            when your subscription expires on{' '}
            <span className={styles.bold}> {cancellationDate}</span>.
          </span>
        </p>

        <div className={styles.featuresWrapper}>
          <div className={styles.features}>
            <span className={styles.featuresTitle}>
              You’ll lose these {currentPlan} features
            </span>

            <div className={styles.featureItem}>
              <span className={styles.icon}>
                <X size={SVG_SIZE_M} />
              </span>
              <span>Access to task history</span>
            </div>

            {(isNewVariant ? newPlansFeatures : features).map((item) => (
              <div className={styles.featureItem} key={item.id}>
                <span className={styles.icon}>
                  <X size={SVG_SIZE_M} />
                </span>
                <span>{item.title}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            fullWidth
            color="primary"
            className={styles.submitButton}
            onClick={onClose}
          >
            No, I'd like to keep the subscription
          </Button>

          <Button color="link" onClick={handleChange}>
            Continue cancellation
          </Button>
        </div>
      </div>
    </Modal>
  );
};
